import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { type KnackApplication } from '@/types/apps';
import { useAppsQuery } from '@/hooks/api/queries/useAppsQuery';
import { formatSlug } from '@/utils/formatters';

function findMatching(searchString: string, apps: KnackApplication[], field: 'name' | 'slug') {
  const lowercaseSearchString = searchString.toLowerCase();

  return apps.filter((app: KnackApplication) =>
    app[field].toLowerCase().includes(lowercaseSearchString)
  );
}

export function useGetUniqueApp() {
  const [t] = useTranslation();
  const { data: appsData = [] } = useAppsQuery();

  const getUniqueName = useCallback(
    (name: string, field: 'name' | 'slug', separator: string) => {
      let matchCount = 0;
      let defaultName = name;

      while (findMatching(defaultName, appsData, field).length) {
        matchCount += 1;
        defaultName = `${name}${separator}${matchCount + 1}`;
      }

      return defaultName.trim();
    },
    [appsData]
  );

  const getUniqueAppName = useCallback(
    (name?: string) => {
      const defaultName = name ?? t('components.create_app.default_app_name');
      return getUniqueName(defaultName, 'name', ' ');
    },
    [getUniqueName, t]
  );

  const getUniqueAppSlug = useCallback(
    (name?: string) => {
      const defaultName = name ?? t('components.create_app.default_app_slug');
      const uniqueName = getUniqueName(defaultName, 'slug', '-');
      return formatSlug(uniqueName);
    },
    [getUniqueName, t]
  );

  return { getUniqueAppName, getUniqueAppSlug };
}
